/**
 * mesas.service.ts — Contrato público del módulo Mesas (espacio + órdenes).
 *
 * QUÉ HACE: Toda lectura/escritura de pisos, zonas, mesas, elementos del plano
 *   y órdenes pasa por aquí. Habla con la API (MSW en demo; backend real cuando
 *   exista) y normaliza respuestas con `unwrapApi*`.
 * QUÉ EXPORTA: mesasService, MesasServiceError.
 * QUÉ NO DEBE HACER: No conoce React, componentes ni almacenamiento directo.
 */
import api from "@/services/api";
import {
  unwrapApiField,
  unwrapApiList,
  unwrapApiPayload,
} from "@/lib/unwrapApiResponse";
import type { CartItem } from "@/types";
import type {
  ComandaMesa,
  CrearOrdenMesaInput,
  EstadoOrdenMesa,
  GuardarPlanoInput,
  Mesa,
  MesaInput,
  OrdenMesa,
  Piso,
  PisoInput,
  PlanoData,
  Zona,
  ZonaInput,
} from "../types/mesas.types";
import { MesasServiceError } from "../types/mesas.types";

/**
 * Contrato público adicional: estados que mantienen la orden activa. Lo usan
 * Caja (bandeja de órdenes) y el KDS sin importar el interior del feature.
 */
export { ESTADOS_ORDEN_ACTIVA } from "../constants/ordenMesaEstados";

/** Contrato público: etiquetas de orden ("Mesa 3 · Terraza" / "Para llevar"). */
export {
  esOrdenParaLlevar,
  etiquetaOrdenMesa,
} from "../utils/ordenMesaHelpers";

const BASE = "mesas";

export interface FiltrosOrdenesMesa {
  estados?: EstadoOrdenMesa[];
  meseroId?: string;
  mesaId?: string;
}

const requireEntidad = <T>(value: T | null | undefined, mensaje: string): T => {
  if (value === null || value === undefined) {
    throw new MesasServiceError(mensaje, "respuesta_invalida");
  }
  return value;
};

export const mesasService = {
  // ── Pisos ─────────────────────────────────────────────────────────────────

  async getPisos(): Promise<Piso[]> {
    const { data } = await api.get(`${BASE}/pisos`);
    return unwrapApiList<Piso>(data, "pisos");
  },

  async createPiso(input: PisoInput): Promise<Piso> {
    const { data } = await api.post(`${BASE}/pisos`, input);
    const piso = unwrapApiPayload<{ piso?: Piso }>(data).piso;
    return requireEntidad(piso, "No se pudo crear el piso");
  },

  async updatePiso(id: string, input: Partial<PisoInput>): Promise<Piso> {
    const { data } = await api.patch(`${BASE}/pisos/${id}`, input);
    const piso = unwrapApiPayload<{ piso?: Piso }>(data).piso;
    return requireEntidad(piso, "No se pudo actualizar el piso");
  },

  async deletePiso(id: string): Promise<void> {
    await api.delete(`${BASE}/pisos/${id}`);
  },

  // ── Zonas ─────────────────────────────────────────────────────────────────

  async getZonas(pisoId?: string): Promise<Zona[]> {
    const query = pisoId ? `?pisoId=${encodeURIComponent(pisoId)}` : "";
    const { data } = await api.get(`${BASE}/zonas${query}`);
    return unwrapApiList<Zona>(data, "zonas");
  },

  async createZona(input: ZonaInput): Promise<Zona> {
    const { data } = await api.post(`${BASE}/zonas`, input);
    const zona = unwrapApiPayload<{ zona?: Zona }>(data).zona;
    return requireEntidad(zona, "No se pudo crear la zona");
  },

  async updateZona(id: string, input: Partial<ZonaInput>): Promise<Zona> {
    const { data } = await api.patch(`${BASE}/zonas/${id}`, input);
    const zona = unwrapApiPayload<{ zona?: Zona }>(data).zona;
    return requireEntidad(zona, "No se pudo actualizar la zona");
  },

  async deleteZona(id: string): Promise<void> {
    await api.delete(`${BASE}/zonas/${id}`);
  },

  // ── Plano completo de un piso ─────────────────────────────────────────────

  /** Espacio completo (piso + zonas + mesas + elementos) para dibujar/editar. */
  async getPlano(pisoId: string): Promise<PlanoData> {
    const { data } = await api.get(`${BASE}/plano/${pisoId}`);
    const payload = unwrapApiPayload<Partial<PlanoData>>(data);
    return {
      piso: requireEntidad(payload.piso, "No se encontró el piso"),
      zonas: payload.zonas ?? [],
      mesas: payload.mesas ?? [],
      elementos: payload.elementos ?? [],
    };
  },

  /** Guardado atómico del plano: upserts + eliminaciones en una transacción. */
  async guardarPlano(input: GuardarPlanoInput): Promise<PlanoData> {
    const { data } = await api.put(`${BASE}/plano`, input);
    const payload = unwrapApiPayload<Partial<PlanoData>>(data);
    return {
      piso: requireEntidad(payload.piso, "No se pudo guardar el plano"),
      zonas: payload.zonas ?? [],
      mesas: payload.mesas ?? [],
      elementos: payload.elementos ?? [],
    };
  },

  // ── Catálogo de mesas ─────────────────────────────────────────────────────

  async getMesas(): Promise<Mesa[]> {
    const { data } = await api.get(`${BASE}/catalogo`);
    return unwrapApiList<Mesa>(data, "mesas");
  },

  async createMesa(input: MesaInput): Promise<Mesa> {
    const { data } = await api.post(`${BASE}/catalogo`, input);
    const mesa = unwrapApiPayload<{ mesa?: Mesa }>(data).mesa;
    return requireEntidad(mesa, "No se pudo crear la mesa");
  },

  async updateMesa(id: string, input: Partial<MesaInput>): Promise<Mesa> {
    const { data } = await api.patch(`${BASE}/catalogo/${id}`, input);
    const mesa = unwrapApiPayload<{ mesa?: Mesa }>(data).mesa;
    return requireEntidad(mesa, "No se pudo actualizar la mesa");
  },

  async deleteMesa(id: string): Promise<void> {
    await api.delete(`${BASE}/catalogo/${id}`);
  },

  // ── Órdenes ───────────────────────────────────────────────────────────────

  async getOrdenes(filtros: FiltrosOrdenesMesa = {}): Promise<OrdenMesa[]> {
    const params = new URLSearchParams();
    if (filtros.estados?.length) {
      params.set("estado", filtros.estados.join(","));
    }
    if (filtros.meseroId) params.set("meseroId", filtros.meseroId);
    if (filtros.mesaId) params.set("mesaId", filtros.mesaId);
    const query = params.toString();
    const { data } = await api.get(
      query ? `${BASE}/ordenes?${query}` : `${BASE}/ordenes`,
    );
    return unwrapApiList<OrdenMesa>(data, "ordenes");
  },

  async getOrden(id: string): Promise<OrdenMesa | null> {
    const { data } = await api.get(`${BASE}/ordenes/${id}`);
    return unwrapApiField<OrdenMesa | null>(data, "orden", null);
  },

  async crearOrden(input: CrearOrdenMesaInput): Promise<OrdenMesa> {
    const { data } = await api.post(`${BASE}/ordenes`, input);
    const orden = unwrapApiPayload<{ orden?: OrdenMesa }>(data).orden;
    return requireEntidad(orden, "No se pudo crear la orden");
  },

  /**
   * Guarda el borrador de la orden: reemplaza los ítems NO enviados y
   * conserva los ya enviados a cocina. Se usa al enviar comanda y al
   * «Poner en espera».
   */
  async guardarBorrador(
    ordenId: string,
    items: CartItem[],
  ): Promise<OrdenMesa> {
    const { data } = await api.patch(`${BASE}/ordenes/${ordenId}/items`, {
      items,
      modo: "borrador",
    });
    const orden = unwrapApiPayload<{ orden?: OrdenMesa }>(data).orden;
    return requireEntidad(orden, "No se pudo guardar la orden");
  },

  /** Marca ítems como enviados y genera la comanda correspondiente. */
  async enviarComanda(
    ordenId: string,
    itemIds: string[],
  ): Promise<{ orden: OrdenMesa; comanda: ComandaMesa }> {
    const { data } = await api.post(`${BASE}/ordenes/${ordenId}/comanda`, {
      itemIds,
    });
    const payload = unwrapApiPayload<{
      orden?: OrdenMesa;
      comanda?: ComandaMesa;
    }>(data);
    return {
      orden: requireEntidad(payload.orden, "No se pudo enviar la comanda"),
      comanda: requireEntidad(payload.comanda, "No se pudo generar la comanda"),
    };
  },

  async cambiarEstado(
    ordenId: string,
    estado: EstadoOrdenMesa,
  ): Promise<OrdenMesa> {
    const { data } = await api.patch(`${BASE}/ordenes/${ordenId}/estado`, {
      estado,
    });
    const orden = unwrapApiPayload<{ orden?: OrdenMesa }>(data).orden;
    return requireEntidad(orden, "No se pudo cambiar el estado");
  },

  async anularOrden(ordenId: string, motivo: string): Promise<OrdenMesa> {
    const { data } = await api.patch(`${BASE}/ordenes/${ordenId}/anular`, {
      motivo,
    });
    const orden = unwrapApiPayload<{ orden?: OrdenMesa }>(data).orden;
    return requireEntidad(orden, "No se pudo anular la orden");
  },

  /** Marca la orden como cobrada desde Caja y la enlaza con la venta. */
  async marcarCobrada(
    ordenId: string,
    venta: { ventaId?: string; cajaId?: string },
  ): Promise<OrdenMesa> {
    const { data } = await api.patch(`${BASE}/ordenes/${ordenId}/cobrada`, {
      ventaId: venta.ventaId,
      cajaId: venta.cajaId,
    });
    const orden = unwrapApiPayload<{ orden?: OrdenMesa }>(data).orden;
    return requireEntidad(orden, "No se pudo marcar la orden como cobrada");
  },
};
